import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import Table, { Column } from "../../components/Table";
import Modal from "../../components/Modal";
import FormBuilder from "../../components/FormBuilder";
import { vehicleFormFields } from "./vehicleFormFields";
import { getVehicles } from "../../services/vehicleService";
import { useNavigate } from "react-router-dom";
import InputField from "../../components/InputField";
import { Icon } from "@iconify/react";
import { getImageUrl } from "../../services/mappers";

type VehicleRow = {
  id: number | string;
  profile?: string | null;
  model?: string;
  vehicle_type?: string;
  vehicle_id?: string;
  registration_number?: string;
  loan_status?: string;
  raw?: any;
  [k: string]: any;
  ownerid?: number | string;
};

export default function Vehicles() {
  const [openAddModal, setAddOpenModal] = useState(false);
  const [openEditModal, setEditOpenModal] = useState(false);
  const [data, setData] = useState<VehicleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string[]> | null>(null);
  const [selected, setSelected] = useState<VehicleRow | null>(null);
  const formRef = useRef<any>(null);
  const navigate = useNavigate();

  // server pagination
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [lastPage, setLastPage] = useState<number>(1);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);

  // Search functionality
  const [searchQuery, setSearchQuery] = useState("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);




  const mapVehicleToRow = (v: any): VehicleRow => {
    const profilePhoto = v.owner?.profile_photo_url;
  const profile = profilePhoto
    ? profilePhoto.startsWith('http')
      ? profilePhoto
      : `${process.env.REACT_APP_API_BASE_URL ?? ""}/${profilePhoto}`
    : null;
    return {
      id: v.vehicle_id ?? v.id ?? v.vehicle_id ?? v.id,
      ownerid: v.owner?.id ?? v.owner_id ?? v.ownerid ?? v.owner?.id,
      vehicle_id: v.vehicle_id ?? v.vehicle_id,
      profile: getImageUrl(v.owner?.profile_photo_url ?? null),

      model: v.manufacturer && v.model ? `${v.manufacturer} ${v.model}` : v.model ?? `${v.variant ?? ""}`.trim(),
      vehicle_type: v.vehicle_type,
      registration_number: v.registration_number,
      raw: v,
      loan_status: Array.isArray(v.loan_status) && v.loan_status.length > 0
        ? v.loan_status[0] // e.g., "inprogress"
        : (v.loan_status ?? null), // Handles non-array or null/undefined

    };
  };

const fetchVehicles = async (p = page, per_page = pageSize, query = searchQuery) => {
  try {
    setLoading(true);
 
    const res = await getVehicles({
      page: p,
      per_page,
      ...(query && { query }) // Include query param if search is active
    });

    // ✅ LARAVEL PAGINATION SHAPE
    const rows = res.data ?? [];

    setData(rows.map(mapVehicleToRow));

    setPage(res.current_page ?? p);
    setPageSize(res.per_page ?? per_page);
    setTotal(res.total ?? 0);
    setFrom(res.from ?? 0);
    setTo(res.to ?? 0);
    setLastPage(res.last_page ?? 1);

  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
};
  const columns: Column<VehicleRow>[] = useMemo(
    () => [
      {
        header: "Vehicle",
        accessor: "model",
        sortable: true,
        cell: (row) => (
          <div className="flex items-center gap-3">
            <div className="relative">
              {row.profile ? (
                <img src={row.profile} alt={row.model} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500">
                  <span className="text-white font-semibold">{row.model?.charAt(0)?.toUpperCase()}</span>
                </div>
              )}
            </div>
            <div>
              <div className="font-medium">{row.model}</div>
            </div>
          </div>
        ),
      },
      {
        header: "Status",
        accessor: "loan_status",
        cell: (row) =>
          row.loan_status ? (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.loan_status === "active" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
              {row.loan_status}
            </span>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          ),
        width: "w-40",
      },
      {
        header: "R_number",
        accessor: "registration_number",
        cell: (row) =>
          row.registration_number ? (
            <span className="text-sm hover:underline">{row.registration_number}</span>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          ),
        width: "w-40",
      },
      {
        header: "Type",
        accessor: "vehicle_type",
        sortable: true,
        cell: (row) => row.vehicle_type ?? <span className="text-sm text-gray-400">—</span>,
      },
    ],
    []
  );

  useEffect(() => {
    fetchVehicles(page, pageSize);
  }, [page, pageSize]);

  // Handle search with debounce
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1); // Reset to first page on new search
      fetchVehicles(1, pageSize, value);
    }, 300); // 300ms debounce delay
  }, [pageSize, searchQuery]);


  const handleAddClick = () => {
    setFormErrors(null);
    setAddOpenModal(true);
    setSelected(null);
    if (formRef.current?.reset) formRef.current.reset();
  };

  const handleEdit = (row: VehicleRow) => {
    setFormErrors(null);
    setSelected(row);
    setEditOpenModal(true);
  };

  const handleDelete = (row: VehicleRow) => {
    // console.log("Delete vehicle (local):", row);
  };

  const handleSubmitAdd = (values: any) => {
    setFormErrors(null);
    console.log("Create vehicle (local):", values);
    setAddOpenModal(false);
  };

  const handleSubmitEdit = (values: any) => {
    if (!selected) return;
    setFormErrors(null);
    const updated: VehicleRow = { ...selected, ...values, registration_number: values.registration_number ?? selected.registration_number };
    setData((d) => d.map((r) => (r.id === selected.id ? updated : r)));
    setEditOpenModal(false);
    setSelected(null);
  };

  return (
    <div className="pt-3">
       <div className="grid grid-cols-1 md:grid-cols-2 mb-4 gap-4 items-center bg-white p-4 rounded-xl shadow-sm">
        <div className="flex justify-between">
          <h2 className="text-2xl font-semibold">Vehicles List</h2>
        </div>
        
        <div className="">
          {/* Search Input */}
          <div className="relative w-full ">
            <InputField
              placeholder="Search by Registration number"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full focus:outline-none focus:ring-2 focus:ring-sky-300 transition"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon icon="mdi:magnify" width={18} className="text-slate-400" />
            </div>
          </div>
        </div>
      
      </div>
      <Table<VehicleRow>
        // title="Vehicles List"
       data={data}
  columns={columns}
  serverSide
  total={total}
  from={from}
  to={to}
  currentPage={page}
  pageSize={pageSize}
  loading={loading}
  onPageChange={(p) => fetchVehicles(p, pageSize)}
  onPageSizeChange={(s) => fetchVehicles(1, s)}
        defaultPageSize={20}
        pageSizeOptions={[20]}
        // loading={loading}
        mobileHeaderFields={["profile", "model", "registration_number"]}
        onRowClick={(row) => navigate("/customers/details", { state: { id: row.ownerid, activeTab: "vehicle" } })}
      />

      {/* Add Modal */}
      <Modal isOpen={openAddModal} onClose={() => setAddOpenModal(false)} title="Add Vehicle">
        <FormBuilder
          ref={formRef}
          fields={vehicleFormFields}
          onSubmit={(values) => {
            handleSubmitAdd(values);
          }}
          onCancel={() => setAddOpenModal(false)}
          submitLabel="Create Vehicle"
          error={formErrors}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={openEditModal} onClose={() => setEditOpenModal(false)} title="Edit Vehicle">
        <FormBuilder
          ref={formRef}
          fields={vehicleFormFields}
          onSubmit={(values) => {
            handleSubmitEdit(values);
          }}
          onCancel={() => setEditOpenModal(false)}
          submitLabel="Update Vehicle"
          error={formErrors}
          defaultValues={selected?.raw ?? selected}
        />
      </Modal>
    </div>
  );
}
